import { useState, useEffect, useCallback, useRef } from "react";
import { useFaust } from "../../contexts/FaustContext";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "../ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { CloudUpload, CloudDownload, Loader2 } from "lucide-react";
import { cn } from "../../lib/utils";

type ExportDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
};

type ExportTargets = { [platform: string]: string[] };

export function ExportDialog({ open, onOpenChange }: ExportDialogProps) {
    const { serverRef, editorRef, libFaustRef, compileOptionsRef, saveCompileOptions } = useFaust();

    const [server, setServer] = useState(serverRef.current);
    const [targets, setTargets] = useState<ExportTargets>({});
    const [platform, setPlatform] = useState("");
    const [arch, setArch] = useState("");
    const [fileName, setFileName] = useState("untitled");
    const [isExporting, setIsExporting] = useState(false);
    const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const qrCanvasRef = useRef<HTMLCanvasElement>(null);

    // Fetch export targets
    useEffect(() => {
        if (!open) return;
        setError(null);
        setDownloadUrl(null);

        fetch(`${server}/targets`)
            .then((r) => r.json())
            .then((t: ExportTargets) => {
                setTargets(t);
                const platforms = Object.keys(t).sort();
                if (platforms.length > 0) {
                    const opts = compileOptionsRef.current;
                    const plat = platforms.includes(opts.exportPlatform)
                        ? opts.exportPlatform
                        : platforms[0];
                    setPlatform(plat);
                    const archs = t[plat];
                    setArch(
                        archs.includes(opts.exportArch)
                            ? opts.exportArch
                            : archs[0] || ""
                    );
                }
            })
            .catch((e) => setError(String(e)));
    }, [open, server, compileOptionsRef]);

    const handlePlatformChange = useCallback(
        (plat: string) => {
            setPlatform(plat);
            compileOptionsRef.current.exportPlatform = plat;
            saveCompileOptions();
            const archs = targets[plat] || [];
            setArch(archs[0] || "");
        },
        [targets, compileOptionsRef, saveCompileOptions]
    );

    const handleArchChange = useCallback(
        (a: string) => {
            setArch(a);
            compileOptionsRef.current.exportArch = a;
            saveCompileOptions();
        },
        [compileOptionsRef, saveCompileOptions]
    );

    const handleExport = useCallback(async () => {
        setIsExporting(true);
        setError(null);
        setDownloadUrl(null);

        try {
            const editor = editorRef.current;
            if (!editor) throw new Error("Editor not ready");

            const JSZip = ((await import("jszip")) as any).default;
            const zip = new JSZip();
            const code = editor.getValue();
            const name = fileName.replace(/[^a-zA-Z0-9_]/g, "") || "untitled";
            zip.file(`${name}.dsp`, `declare filename "${name}.dsp";\ndeclare name "${name}";\n${code}`);

            const blob = await zip.generateAsync({ type: "blob" });
            const form = new FormData();
            form.append("file", new File([blob], `${name}.zip`));

            const uploadResponse = await fetch(`${server}/filepost`, {
                method: "POST",
                body: form,
            });
            const shaKey = await uploadResponse.text();

            if (!shaKey.match(/^[0-9A-Fa-f]+$/)) {
                throw new Error(shaKey);
            }

            let target = "binary.zip";
            if (arch === "pwa" || arch === "pwa-poly") target = "index.html";
            else if (platform === "android") target = "binary.apk";

            const path = `${server}/${shaKey}/${platform}/${arch}`;
            const href = `${path}/${target}`;

            const compileResponse = await fetch(`${path}/precompile`);
            const result = await compileResponse.text();

            if (result === "DONE") {
                setDownloadUrl(href);
                // Render QR code
                if (qrCanvasRef.current) {
                    const QRCode = await import("qrcode");
                    QRCode.toCanvas(qrCanvasRef.current, href);
                }
            } else {
                throw new Error(result);
            }
        } catch (e: any) {
            setError(e.message || String(e));
        } finally {
            setIsExporting(false);
        }
    }, [server, platform, arch, fileName, editorRef]);

    const platforms = Object.keys(targets).sort();
    const architectures = targets[platform] || [];

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md bg-[#1f1f1f] border-white/[0.08]">
                <DialogHeader>
                    <DialogTitle className="text-zinc-100 font-display">Export as</DialogTitle>
                </DialogHeader>

                <div className="flex flex-col gap-4 py-2">
                    {/* Server */}
                    <div className="flex flex-col gap-1.5">
                        <Label className="text-xs text-zinc-500">Server</Label>
                        <Input
                            value={server}
                            onChange={(e) => {
                                setServer(e.target.value);
                                serverRef.current = e.target.value;
                            }}
                            className="h-8 text-xs bg-black border-white/[0.08]"
                        />
                    </div>

                    {/* File Name */}
                    <div className="flex flex-col gap-1.5">
                        <Label className="text-xs text-zinc-500">File Name</Label>
                        <div className="flex">
                            <Input
                                value={fileName}
                                onChange={(e) =>
                                    setFileName(e.target.value.replace(/[^a-zA-Z0-9_]/g, ""))
                                }
                                className="h-8 text-xs bg-black border-white/[0.08] rounded-r-none"
                            />
                            <span className="flex items-center px-2 h-8 bg-zinc-800 border border-l-0 border-zinc-700 rounded-r text-xs text-zinc-500">
                                .dsp
                            </span>
                        </div>
                    </div>

                    {/* Platform */}
                    <div className="flex flex-col gap-1.5">
                        <Label className="text-xs text-zinc-500">Platform</Label>
                        <Select value={platform} onValueChange={handlePlatformChange}>
                            <SelectTrigger className="h-8 text-xs bg-black border-white/[0.08]">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {platforms.map((p) => (
                                    <SelectItem key={p} value={p}>
                                        {p}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Architecture */}
                    <div className="flex flex-col gap-1.5">
                        <Label className="text-xs text-zinc-500">Architecture</Label>
                        <Select value={arch} onValueChange={handleArchChange}>
                            <SelectTrigger className="h-8 text-xs bg-black border-white/[0.08]">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {architectures.map((a) => (
                                    <SelectItem key={a} value={a}>
                                        {a}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                        <Button
                            onClick={handleExport}
                            disabled={isExporting || !platform || !arch}
                            className="bg-[#0078d4] hover:bg-[#0078d4]/90 text-white gap-2"
                        >
                            {isExporting ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <CloudUpload className="h-4 w-4" />
                            )}
                            Compile
                        </Button>

                        {downloadUrl && (
                            <Button
                                variant="outline"
                                className="border-green-700 text-green-400 hover:bg-green-400/10 gap-2"
                                onClick={() => window.open(downloadUrl, "_blank")}
                            >
                                <CloudDownload className="h-4 w-4" />
                                Download
                            </Button>
                        )}
                    </div>

                    {/* QR Code */}
                    {downloadUrl && (
                        <div className="flex justify-center">
                            <canvas
                                ref={qrCanvasRef}
                                width={200}
                                height={200}
                                className="rounded border border-zinc-800"
                            />
                        </div>
                    )}

                    {/* Error */}
                    {error && (
                        <div className="text-xs text-red-400 bg-red-950/50 border border-red-900/50 rounded px-3 py-2">
                            {error}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
